import { User } from "model/userTypes";
import { create } from "zustand";

// =============================================================================
// State Types
// =============================================================================

interface AppState {
  // Auth state
  user: User | null;
  isAuthenticated: boolean;
  isLoggingIn: boolean;
  authError: boolean;

  // App state
  initialized: boolean;
  currentFarmId: string | null;
  locale: string;
  loading: "idle" | "pending";
  loadingStatus: string;
  loadingProgress: number;
  loadingTotal: number;

  // Auth actions
  setUser: (user: User | null) => void;
  setAuthenticated: (value: boolean) => void;
  setLoggingIn: (value: boolean) => void;
  setAuthError: (value: boolean) => void;
  addFarmToUser: (farmId: string) => void;
  removeFarmFromUser: (farmId: string) => void;

  // App actions
  setInitialized: (value: boolean) => void;
  setCurrentFarmId: (farmId: string | null) => void;
  setLocale: (locale: string) => void;
  setLoading: (loading: "idle" | "pending") => void;
  setLoadingStatus: (status: string) => void;
  setLoadingProgress: (progress: number, total: number) => void;

  reset: () => void;
}

const initialState = {
  // Auth
  user: null,
  isAuthenticated: false,
  isLoggingIn: false,
  authError: false,
  // App
  initialized: false,
  currentFarmId: null,
  locale: "en",
  loading: "idle" as const,
  loadingStatus: "",
  loadingProgress: 0,
  loadingTotal: 0,
};

export const useAppStore = create<AppState>((set) => ({
  ...initialState,

  // Auth actions
  setUser: (user) => set({ user }),
  setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  setLoggingIn: (isLoggingIn) => set({ isLoggingIn }),
  setAuthError: (authError) => set({ authError }),
  addFarmToUser: (farmId) =>
    set((state) => ({
      user: state.user
        ? { ...state.user, farms: [...(state.user.farms || []), farmId] }
        : null,
    })),
  removeFarmFromUser: (farmId) =>
    set((state) => ({
      user: state.user
        ? {
            ...state.user,
            farms: (state.user.farms || []).filter((id) => id !== farmId),
          }
        : null,
    })),

  // App actions
  setInitialized: (initialized) => set({ initialized }),
  setCurrentFarmId: (currentFarmId) => set({ currentFarmId }),
  setLocale: (locale) => set({ locale }),
  setLoading: (loading) => set({ loading }),
  setLoadingStatus: (loadingStatus) => set({ loadingStatus }),
  setLoadingProgress: (loadingProgress, loadingTotal) =>
    set({ loadingProgress, loadingTotal }),

  reset: () => set(initialState),
}));

// =============================================================================
// Typed Selectors
// =============================================================================
// Use these instead of inline selectors to get autocomplete and prevent typos.
// Example: const user = useAppStore(selectUser);

// Auth selectors
export const selectUser = (state: AppState) => state.user;
export const selectIsAuthenticated = (state: AppState) => state.isAuthenticated;
export const selectIsLoggingIn = (state: AppState) => state.isLoggingIn;
export const selectAuthError = (state: AppState) => state.authError;

// App selectors
export const selectInitialized = (state: AppState) => state.initialized;
export const selectCurrentFarmId = (state: AppState) => state.currentFarmId;
export const selectLocale = (state: AppState) => state.locale;
export const selectLoading = (state: AppState) => state.loading;
export const selectLoadingStatus = (state: AppState) => state.loadingStatus;
export const selectLoadingProgress = (state: AppState) => ({
  progress: state.loadingProgress,
  total: state.loadingTotal,
});

// Derived selectors
export const selectUserEmail = (state: AppState) => state.user?.mail ?? null;
export const selectUserFarmIds = (state: AppState) => state.user?.farms ?? [];
export const selectIsLoading = (state: AppState) => state.loading === "pending";
